/** 连接 Drei 资源加载进度、数字动画和 CSS 进度条的启动加载页。 */
import { useProgress } from '@react-three/drei';
import { useCountUp } from 'react-countup';
import classNames from 'classnames';
import { usePreloaderReadyState } from '../../../atoms/preloader-ready';
import { useDebounce } from '../../../hooks/use-debounce';
import { useEffect, useRef } from 'react';
import { supportsWebp } from '../../../utils/supports-webp';
import { useGpuState } from '../../../atoms/gpu';

const Preloader = () => {
    const counterRef = useRef<HTMLDivElement>(null);

    // useProgress 汇总由 Drei/Three loader 管理的模型、纹理等资源加载百分比。
    const progress = useProgress((state) => state.progress);
    // 达到 100 后再稳定 1.3 秒才离场，避免最后一个资源抖动造成闪屏。
    const debouncedProgress = useDebounce(progress, 1300);
    const [_, setPreloaderReady] = usePreloaderReadyState();
    const [gpuState] = useGpuState();

    // react-countup 直接更新 ref 中的数字，不必让 React 每一帧重新渲染。
    const { update } = useCountUp({
        ref: counterRef,
        start: 0,
        end: 0,
        duration: 1,
        useEasing: true,
    });

    useEffect(() => {
        // GPU 检测完成后才显示真实进度，确保启动顺序一致。
        if (gpuState) {
            update(progress);
        }
    }, [update, gpuState, progress]);

    useEffect(() => {
        // 通知 Dom：可以给加载页添加离场状态并最终卸载。
        if (debouncedProgress === 100) {
            setPreloaderReady(true);
        }
    }, [debouncedProgress, setPreloaderReady]);

    return (
        <div
            className={classNames('preloader', {
                'preloader--leaving': debouncedProgress === 100,
            })}
        >
            <div className="preloader-inner-el">
                <img
                    src={`/img/paints/orange.${supportsWebp ? 'webp' : 'jpg'}`}
                    width={1920}
                    height={960}
                    alt=""
                    className="preloader-bg"
                />
                <img
                    src={`/img/happy-ny-white.${supportsWebp ? 'webp' : 'png'}`}
                    width={716}
                    height={560}
                    alt=""
                    className="img-fluid preloader-img"
                />
                <div className="preloader-bottom">
                    <div className="preloader-progress">
                        <span ref={counterRef}>0</span>%
                    </div>
                    <div className="preloader-progressbar">
                        <div
                            className="preloader-progressbar__item"
                            // 0～100 映射成 0～1 的横向缩放比例。
                            style={{
                                transform: `scaleX(${
                                    Math.round(progress) / 100
                                })`,
                            }}
                        ></div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Preloader;
